import React, { createContext, useContext, useState } from 'react'
import { useLocalStorage } from 'react-use'

// the current location of the user if they allow tracking via browser
export interface IBrowserLocation {
  /**
   * longitute
   */
  lon?: number
  /**
   * latitude
   */
  lat?: number
  /**
   * @example 'New York'
   */
  cityName?: string
  /**
   * @example 'NY'
   */
  stateCode?: string
}

interface IBrowserLocationContextState {
  /**
   * Current browser location, can be set manually or automatically
   */
  browserLocation?: IBrowserLocation
  /**
   * Indicates if the browser location has city and state information to display
   */
  hasLocationName: boolean
  /**
   * Function to set current browser location
   */
  setBrowserLocation: (browserLocation?: IBrowserLocation) => void
}

export const BrowserLocationContext = createContext<IBrowserLocationContextState>({
  browserLocation: {},
  hasLocationName: false,
  setBrowserLocation: () => {},
})

export const BrowserLocationProvider = ({
  children,
  initialState,
}: {
  initialState?: Partial<IBrowserLocationContextState>
  children: JSX.Element | JSX.Element[]
}): JSX.Element => {
  const [localBrowserLocation, setLocalBrowserLocation] = useLocalStorage<IBrowserLocation>(
    'FMS_BROWSER_LOCATION'
  )

  /**
   * If there is a location stored locally, use that by default
   */
  const [browserLocation, setBrowserLocationValue] = useState<IBrowserLocation | undefined>(
    localBrowserLocation
  )
  const { cityName, stateCode } = browserLocation || {}
  const hasLocationName = Boolean(cityName && stateCode)

  /**
   * Save browser location into local storage and context
   */
  const setBrowserLocation = (newBrowserLocation?: IBrowserLocation) => {
    setBrowserLocationValue(newBrowserLocation)
    setLocalBrowserLocation(newBrowserLocation)
  }

  const value = {
    ...initialState,
    browserLocation,
    hasLocationName,
    setBrowserLocation,
  }

  return <BrowserLocationContext.Provider value={value}>{children}</BrowserLocationContext.Provider>
}

export const useBrowserLocationContext = () => useContext(BrowserLocationContext)
